/**
 * Numbers and dates of the Permit screens in the reader's language (react-intl's locale), in place of lib/format's
 * `num` and `date` (which use the browser's): `const f = useFormat(); f.num(4.2, 1)`, `f.date(iso)`, `f.lux(500)`.
 */
import { useMemo } from "react";
import { useIntl } from "react-intl";

export interface PermitFormat {
  /** A number with exactly `digits` decimals ("—" for null). */
  num: (value: number | null | undefined, digits?: number) => string;
  /** A date ("Oct 20, 2026"), with the time when asked ("—" for null). */
  date: (value: string | null | undefined, withTime?: boolean) => string;
  /** A Lux amount from hundredths: "5", "0.50" (no unit: the message says "Lux"). */
  luxNumber: (hundredths: number) => string;
  /** `(v, d) => …` for the lib helpers that take a number formatter (formatLength, specLine). */
  digits: (value: number, digits: number) => string;
}

export function useFormat(): PermitFormat {
  const intl = useIntl();
  return useMemo(() => {
    const digits = (value: number, d: number) => intl.formatNumber(value, { minimumFractionDigits: d, maximumFractionDigits: d });
    return {
      digits,
      num: (value, d = 0) => (value == null ? "—" : digits(value, d)),
      date: (value, withTime = false) => {
        if (!value) return "—";
        const d = new Date(value);
        return withTime
          ? intl.formatDate(d, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
          : intl.formatDate(d, { year: "numeric", month: "short", day: "numeric" });
      },
      luxNumber: (h) => digits(h / 100, h % 100 === 0 ? 0 : 2),
    };
  }, [intl]);
}
